import React, { useState, useEffect } from 'react';

const ImprovGame = ({ socket, sessionId, setGameStarted, setRounds, setCurrentRound, setPlayerRole, setLeaderboard, setIsEndScene, setIsSpeaker, setCurrentLine, setSentGuess, setIsEndGame, role, players, leaderboard, setGuessesMade, setGameMode, setAvailableImprovScripts, setSelectedImprovScript }) => {
    const [activeVote, setActiveVote] = useState(null);
    const [voteBudgetRemaining, setVoteBudgetRemaining] = useState({});
    const [votedLineIds, setVotedLineIds] = useState({});
    const [lineVoteFeed, setLineVoteFeed] = useState([]);

    useEffect(() => {
        if (socket) {
            socket.on('availableImprovScripts', ({ packs }) => {
                setAvailableImprovScripts(packs || []);
                if (packs && packs.length > 0 && setSelectedImprovScript) {
                    setSelectedImprovScript(packs[0]);
                }
                console.log('Available Improv script packs:', packs);
            });

            socket.on('gameStarted', ({ rounds, roles, currentround, gameMode }) => {
                setRounds(rounds);
                setGameStarted(true);
                setCurrentRound(currentround);
                setIsSpeaker(false);
                setIsEndGame(false);
                setIsEndScene(false);
                setActiveVote(null);
                setVotedLineIds({});
                setLineVoteFeed([]);

                if (gameMode) {
                    setGameMode(gameMode);
                }
                
                if (Object.keys(leaderboard).length === 0) {
                    console.log("Creating new leaderboard");
                    const newLeaderboard = {};
                    players.forEach(player => {
                        newLeaderboard[player.name] = 0;
                    });
                    setLeaderboard(newLeaderboard);
                }
                
                if (role === 'player') {
                    const playerRole = roles[socket.id];
                    setPlayerRole(playerRole);
                }
            });

            socket.on('roundStarted', ({ currentRound, roles }) => {
                setCurrentRound(currentRound);
                
                if (socket && socket.id && roles[socket.id]) {
                    const playerRole = roles[socket.id];
                    setPlayerRole(playerRole);
                } else {
                    console.error('Unable to set player role:', { socketId: socket?.id, roles });
                }

                setIsEndScene(false);
                setIsSpeaker(false);
                setCurrentLine(null);
                setSentGuess(false);
                setActiveVote(null);
                setVotedLineIds({});
            });

            socket.on('updateLine', ({ line, isAdlib, isSpeaker }) => {
                setCurrentLine({ text: line, isAdlib });
                setIsSpeaker(isSpeaker);
                setIsEndScene(false);
            });

            socket.on('endScene', () => {
                setIsEndScene(true);
                setIsSpeaker(false);
            });

            socket.on('endGame', () => {
                setGameStarted(false);
                setIsEndGame(true);
            });
            socket.on('guessMade', ({ name, guess, correct }) => {
                setGuessesMade(prevGuesses => [...prevGuesses, { name, guess, correct }]);
            });

            socket.on('adlibVoteOpened', ({ lineId, speakerName, currentRound }) => {
                setActiveVote({ lineId, speakerName, currentRound });
            });

            socket.on('adlibVoteClosed', ({ lineId }) => {
                setActiveVote((previousVote) => {
                    if (!previousVote || previousVote.lineId !== lineId) {
                        return previousVote;
                    }
                    return null;
                });
            });

            socket.on('adlibVoteRecorded', (voteRecord) => {
                setLineVoteFeed((previousFeed) => [...previousFeed, voteRecord]);
            });

            socket.on('voteBudgetUpdate', ({ budgets }) => {
                setVoteBudgetRemaining(budgets || {});
            });

            socket.on('voteBudgetPenaltyApplied', ({ penalties }) => {
                const penaltyEntries = Object.entries(penalties || {}).map(([name, value]) => ({
                    voterName: 'System',
                    speakerName: name,
                    vote: -Math.abs(value),
                    penalty: true,
                    lineId: `penalty-${name}`
                }));
                if (penaltyEntries.length > 0) {
                    setLineVoteFeed((previousFeed) => [...previousFeed, ...penaltyEntries]);
                }
            });

            socket.on('updatePoints', ({ points }) => {
                setLeaderboard(prevLeaderboard => ({
                    ...prevLeaderboard,
                    ...points
                }));
                console.log("Updating leaderboard");
            });

            if (role === 'host') {
                socket.emit('getAvailableImprovScripts');
            }
            
        }

        return () => {
            if (socket) {
                socket.off('gameStarted');
                socket.off('roundStarted');
                socket.off('updateLine');
                socket.off('endScene');
                socket.off('endGame');
                socket.off('guessMade');
                socket.off('adlibVoteOpened');
                socket.off('adlibVoteClosed');
                socket.off('adlibVoteRecorded');
                socket.off('voteBudgetUpdate');
                socket.off('voteBudgetPenaltyApplied');
                socket.off('availableImprovScripts');
            }
        };
    }, [socket]);

    const nextLine = () => {
        if (socket) {
            socket.emit('nextLine', { sessionId: sessionId.toUpperCase() });
        }
    };

    const guessAdlibber = (guess) => {
        setSentGuess(true);
        if (socket) {
            socket.emit('guessAdlibber', { sessionId: sessionId.toUpperCase(), guess });
        }
    };

    const voteAdlibLine = (lineId, vote) => {
        if (socket) {
            socket.emit('voteAdlibLine', {
                sessionId: sessionId.toUpperCase(),
                lineId,
                vote
            });
            setVotedLineIds((previousVotes) => ({
                ...previousVotes,
                [lineId]: true
            }));
        }
    };

    return {
        nextLine,
        guessAdlibber,
        voteAdlibLine,
        activeVote,
        voteBudgetRemaining,
        votedLineIds,
        lineVoteFeed
    };
};

export default ImprovGame;